import React, { useState, useContext } from 'react';
import { useQueryClient } from 'react-query';
import {
  useReports,
  deleteReportQuery,
  QUERY_KEYS as REPORTS_QUERY_KEYS
} from 'src/queries/reports';
import { QUERY_KEYS as ACCOUNT_QUERY_KEYS } from 'src/queries/account';
import { useTree } from 'src/queries/tree';
import appUtils from 'src/components/appUtils';
import { Base, toast } from 'src/components/';
import { AlertModal } from 'src/componentsTailwind/index';
import { UserProfileContext } from 'src/pagesDashboard/UserProfile/context/UserProfileProvider';
import FilterView from 'src/containers/UserProfile/FilterView/FilterView';
import STYLE from 'src/constants/style';
import commonDateUtils from 'common/commonDateUtils';
import commonTreeUtils from 'common/commonTreeUtils';
import COMMON_CONSTANTS from 'common/commonConstants';
import { Table } from 'src/componentsTailwind';
import { TYPES } from 'src/componentsTailwind/Table/Table';
import { BADGE_COLOR_CLASSES } from 'src/componentsTailwind/tailwindConstants';
import commonQuestions from 'common/commonQuestions';
import { useCompany } from 'src/queries/company';
import { get } from 'lodash';
import { useNavigate } from 'react-router-dom';

const { REPORT_TYPE } = COMMON_CONSTANTS;

const Reports = ({ userId }) => {
  const navigate = useNavigate();
  const loggedUser = appUtils.getLoggedUser();
  const queryClient = useQueryClient();
  const { context } = useContext(UserProfileContext);
  const { range } = context;
  const { mutateAsync: deleteReportFn, isLoading: isDeleteReportLoading } = deleteReportQuery();

  const [currentPage, setCurrentPage] = useState(1);
  const {
    data: reportsData,
    isFetching: isFetchingReports,
    isRefetching: isRefetchingReports,
    isError: isErrorReports
  } = useReports(
    {
      userId,
      companyid: loggedUser.companyid,
      lastUpdated: {
        start: range.start,
        end: range.end
      }
    },
    {
      page: {
        number: currentPage,
        size: 10
      }
    },
    {
      keepPreviousData: true
    }
  );

  const [deleteModalId, setDeleteModalId] = useState(null);

  const {
    data: { tree, deleted },
    isFetching: isFetchingTree,
    isError: isErrorTree
  } = useTree();

  const {
    data: company,
    isFetching: isFetchingCompany,
    isError: isErrorCompany
  } = useCompany();

  const doesReportsDataExist = get(reportsData, 'data', null) && get(reportsData, 'meta', null);
  const isInitialFetchingReports = isFetchingReports && !isRefetchingReports && !doesReportsDataExist;
  const isFetching = isFetchingTree || isInitialFetchingReports || isFetchingCompany;
  const isError = isErrorTree || isErrorReports || isErrorCompany;
  const isReady = tree && tree.id && company && company.id && !isFetching && !isError;

  if (!isReady) {
    return null;
  }

  const viewReport = (report) => {
    const encoded = appUtils.encodeURIString(report._id || report.id);
    return navigate(`/dashboard/profile/${userId}/report/${encoded}/preview`);
  };

  const onDeleteReport = async (reportId) => {
    try {
      await deleteReportFn(reportId);
      setDeleteModalId(null);
      queryClient.invalidateQueries(ACCOUNT_QUERY_KEYS.TASKS);
      queryClient.invalidateQueries(REPORTS_QUERY_KEYS.REPORTS);
    } catch (error) {
      toast.error(error ?? 'Could not delete report. Please try again later.');
      setDeleteModalId(null);
    }
  };

  const closeDeleteModal = () => setDeleteModalId(false);

  const { data: reports, meta } = reportsData;
  const { page } = meta;

  const pagination = {
    currentPage,
    setCurrentPage,
    totalPages: page.totalPages
  };

  const columns = [
    {
      label: 'Last Updated',
      span: 1.5
    },
    {
      label: 'Reviewer',
      span: 1.5
    },
    {
      label: 'Reviewee',
      span: 1.5
    },
    {
      type: TYPES.BADGES,
      label: 'Roles',
      span: 1.75
    },
    {
      label: 'Type'
    },
    {
      type: TYPES.BADGES,
      label: 'Status'
    },
    {
      type: TYPES.ACTION,
      label: 'View',
      hidden: true,
      span: 0.5,
      tooltip: false
    },
    {
      type: TYPES.ACTION,
      label: 'Delete',
      hidden: true,
      span: 0.5,
      tooltip: false
    }
  ];

  const rows = reports.map((report) => {
    const authorNode = commonTreeUtils.findNodeById(tree, report.createdBy)
      || deleted.find((node) => node.id === report.createdBy);

    const revieweeNode = commonTreeUtils.findNodeById(tree, report.user)
      || deleted.find((node) => node.id === report.user);

    let roleBadges = [
      {
        label: 'All Roles',
        colorClasses: BADGE_COLOR_CLASSES.GRAY
      }
    ];

    if (report.roles.length > 0) {
      const roleIdsToShow = report.roles.slice(0, 3);
      const roleObjects = roleIdsToShow.map((roleId) => commonQuestions.getRoleById(roleId, company.questions));
      roleBadges = roleObjects.map((roleObject) => ({
        label: roleObject.label,
        colorClasses: BADGE_COLOR_CLASSES.GRAY
      }));
      if (report.roles.length > 3) {
        roleBadges.push({
          label: `and more`,
          color: BADGE_COLOR_CLASSES.GRAY
        });
      }
    }

    return [
      {
        label: commonDateUtils.unixToMonthDayYearFormat(report.lastUpdated)
      },
      {
        label: authorNode.name
      },
      {
        label: revieweeNode.name
      },
      {
        type: TYPES.BADGES,
        badges: roleBadges
      },
      {
        label:
          report.user === authorNode.id
            ? REPORT_TYPE.SELF_REVIEW
            : REPORT_TYPE.REVIEW
      },
      {
        type: TYPES.BADGES,
        badges: [
          {
            label: report.status,
            color: BADGE_COLOR_CLASSES.GREEN
          }
        ]
      },
      {
        type: TYPES.ACTION,
        label: 'View',
        onClick: () => viewReport(report)
      },
      {
        type: TYPES.ACTION,
        label: 'Delete',
        onClick: () => setDeleteModalId(report._id || report.id)
      }
    ];
  });

  const isLoading = isRefetchingReports || isDeleteReportLoading;

  return (
    <>
      <FilterView
        userId={userId}
        show={['date']}
        alwaysDisplayFields={['date']}
      />
      <Base classes={STYLE.CONTAINER_WHITE} loading={isLoading}>
        <div className='flex justify-between'>
          <div className='w-3/5'>
            <h5 className='text-black text-left text-xl mb-2'>Reviews</h5>
          </div>
        </div>
        <Table
          columns={columns}
          rows={rows}
          pagination={pagination}
          placeholderMessage='You have no reports available for this team member'
        />
      </Base>
      {deleteModalId ? (
        <AlertModal
          isLoading={isDeleteReportLoading}
          isOpen={deleteModalId}
          close={closeDeleteModal}
          onAction={() => onDeleteReport(deleteModalId)}
          title='Delete Report'
          content='Are you sure you would like to delete this report?'
        />
      ) : null}
    </>
  );
};

export default Reports;
